/**
 * Burning bush coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const burningBushColoringPagesPack: ToilePack = {
    slug: 'burning-bush-coloring-pages',
    category: 'faith',
    name: 'Burning bush coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bush', label: 'The bush', subject: 'Moses the shepherd with his flock on the mountainside, turning to look at a bush wrapped in flames that do not burn it'},
        {key: 'sandals', label: 'Holy ground', subject: 'Moses kneeling and taking off his sandals before the flaming bush, his staff laid on the ground'},
        {key: 'staff', label: 'The staff', subject: 'Moses watching his staff turn into a serpent on the ground at the foot of the mountain, the bush still glowing behind'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['night sky', 'sheep flock', 'mountain view'],
    directionHint: 'try: night sky',
    seo: {
        title: 'Burning Bush Coloring Pages: The Bush, Holy Ground, Staff',
        metaDescription: "Nine burning bush coloring pages for Sunday school from Exodus 3 and 4: the flaming bush, holy ground and Moses' staff, in three line styles.",
        h1: 'Burning bush coloring pages: the bush, holy ground, the staff',
        tagline: 'Nine pages of Moses and the burning bush in three scenes, each in three line weights.',
        intro: 'Printable burning bush coloring pages for Sunday school and Christian homeschool, from Exodus 3 and 4. Three scenes: Moses the shepherd turning to a bush in flames that do not burn it, Moses taking off his sandals on holy ground, and his staff becoming a serpent at the foot of the mountain. Bold, classic and fine-detail lines. Type a different scene or detail, and the same styles carry it.',
        useCases: [
            'a Sunday school lesson on Exodus 3',
            "a children's church craft table",
            'a Christian homeschool Exodus unit',
            'a Bible story activity sheet',
        ],
        faq: [
            {q: 'Which part of the story do these show?', a: 'Exodus 3 and 4: the bush, the sandals taken off on holy ground, and the staff that becomes a serpent.'},
            {q: 'Is the bush drawn in flames?', a: 'Yes. The bush is wrapped in flames that do not burn it.'},
            {q: 'Can I ask for a night sky or a flock?', a: 'Yes. Type the setting you want. The request shapes the page, but a result is not promised to match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
